import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Fuel, Gauge, IdCard, Siren } from "lucide-react";
import { PageHeader, Loading, Panel, Plate, Chip, useFetch } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { INSP_DRAFT, MyLicence, NewInspection, InspectionView, DeclareSinistre, SinistreList } from "@/components/ComSpace";
import { CongesBlock, MyVehicleDocs } from "@/components/Conges";
import { CapSlot, Thumbs } from "@/components/CapSlot";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fDate, fNum } from "@/lib/api";

function Vehicle() {
  const { data, reload } = useFetch("/m2/inspections");
  const [n, setN] = useState(() => !!localStorage.getItem(INSP_DRAFT));
  if (!data) return <Loading />;
  const v = data.vehicle;
  return (
    <div className="space-y-4" data-testid="my-vehicle">
      <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm space-y-1">
        {v ? <><div className="flex items-center gap-2"><Plate>{v.plate}</Plate><b>{v.brand} {v.model}</b></div><div>Dernier kilométrage : {v.mileage ? `${fNum(v.mileage)} km` : "—"}</div></> : <span>Aucun véhicule affecté</span>}
      </div>
      {v && <CongesBlock />}
      {v && !n && <Button onClick={() => setN(true)} className="w-full h-12 bg-[#0E2D43]" data-testid="new-inspection-btn">{data.inspections.length ? "Nouvel état des lieux" : "Réaliser le premier état des lieux"}</Button>}
      {n && <NewInspection slots={data.slots} reasons={data.reasons} onDone={() => { setN(false); reload(); }} />}
      <Panel title="Mes états des lieux" testid="my-inspections">
        {!data.inspections.length ? <p className="text-sm text-slate-500">Aucun état des lieux.</p> : data.inspections.map((i) => <div key={i.id} className="border-b last:border-0 py-3"><InspectionView i={i} slots={data.slots} testid={`my-insp-${i.id}`} /></div>)}
      </Panel>
      {v && <MyVehicleDocs />}
    </div>
  );
}

function Sinistres() {
  const { user } = useAuth();
  const nav = useNavigate();
  const me = useFetch("/m2/inspections");
  const list = useFetch("/m2/sinistres");
  const [open, setOpen] = useState(() => !!localStorage.getItem("draft_sinistre"));
  return (
    <div className="space-y-4" data-testid="my-sinistres">
      {!open ? <Button onClick={() => setOpen(true)} className="w-full h-14 bg-rose-600 hover:bg-rose-700 text-white text-base font-bold" data-testid="declare-sinistre-btn"><Siren className="w-5 h-5 mr-2" /> Déclarer un accident / sinistre</Button>
        : <DeclareSinistre vehicle={me.data?.vehicle} user={user} onDone={(id) => nav(`/commerciaux/sinistres/${id}`)} />}
      <Panel title="Mes dossiers" testid="my-sinistres-panel"><SinistreList rows={list.data} /></Panel>
    </div>
  );
}

export default function MonEspace() {
  const [sp] = useSearchParams();
  return (
    <div className="max-w-3xl" data-testid="mon-espace">
      <PageHeader overline="Véhicules commerciaux" title="Mon espace" />
      <div className="grid grid-cols-2 gap-2 mb-4">
        <Link to="/commerciaux/releve" className="h-12 rounded-xl border bg-white flex items-center justify-center gap-2 font-semibold text-[#0E2D43]" data-testid="espace-releves"><Gauge className="w-4 h-4" /> Mes relevés</Link>
        <Link to="/commerciaux/plein" className="h-12 rounded-xl border bg-white flex items-center justify-center gap-2 font-semibold text-[#0E2D43]" data-testid="espace-pleins"><Fuel className="w-4 h-4" /> Mes pleins</Link>
      </div>
      <Tabs defaultValue={sp.get("tab") || (localStorage.getItem("draft_sinistre") ? "sinistres" : "vehicule")}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="vehicule" data-testid="tab-espace-vehicule">Mon véhicule</TabsTrigger>
          <TabsTrigger value="permis" data-testid="tab-espace-permis"><IdCard className="w-4 h-4 mr-1" />Mon permis</TabsTrigger>
          <TabsTrigger value="sinistres" data-testid="tab-espace-sinistres">Sinistres</TabsTrigger>
        </TabsList>
        <TabsContent value="vehicule"><Vehicle /></TabsContent>
        <TabsContent value="permis"><MyLicence /></TabsContent>
        <TabsContent value="sinistres"><Sinistres /></TabsContent>
      </Tabs>
    </div>
  );
}

export function MonSinistre() {
  const { id } = useParams();
  const { data, reload } = useFetch(`/m2/sinistres/${id}`);
  const [ph, setPh] = useState([]);
  const [txt, setTxt] = useState("");
  if (!data) return <Loading />;
  const send = async () => {
    try {
      await api.patch(`/m2/sinistres/${id}/complement`, { comment: txt, add_photos: ph.map((file_id) => ({ file_id, category: "autre" })) });
      toast.success("Complément ajouté"); setPh([]); setTxt(""); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const yn = (b) => (b ? "Oui" : "Non");
  return (
    <div className="max-w-3xl space-y-4" data-testid="mon-sinistre">
      <PageHeader overline="Mon espace · Sinistres" title={`Dossier ${data.number}`} />
      {!data.drivable && <div className="rounded-lg bg-rose-600 text-white font-bold p-3">ALERTE — VÉHICULE IMMOBILISÉ</div>}
      <div className="bg-white border rounded-xl p-4 text-sm space-y-1">
        <div className="flex flex-wrap items-center gap-2"><Plate>{data.plate}</Plate><Chip testid="sinistre-status">{data.status_label}</Chip><span>{fDate(data.date)} {data.time}</span></div>
        <div>Lieu : {data.location}</div><div>Faits : {data.description}</div>
        <div>Tiers : {yn(data.third_party)} · Responsabilité : {{ oui: "Oui", non: "Non", a_determiner: "À déterminer" }[data.responsible]} · Roulant : {yn(data.drivable)} · Constat : {yn(data.amicable)} · Forces de l'ordre : {yn(data.police)} · Réparation : {{ oui: "Oui", non: "Non", a_determiner: "À déterminer" }[data.repair_status] || "—"}</div>
        {data.comment && <div>Commentaire : {data.comment}</div>}
        {(data.complements || []).map((c, i) => <div key={i} className="text-xs text-slate-600">Complément du {fDate(c.at)} : {c.text}</div>)}
        <Thumbs ids={data.photos.map((p) => p.file_id)} testid="sinistre-photos" />
      </div>
      {data.status !== "clos" && (
        <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="sinistre-complement">
          <b className="text-[#0E2D43]">Ajouter des photos ou informations</b>
          <CapSlot label="Photos" ids={ph} onChange={setPh} testid="complement-photos" />
          <Input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Information complémentaire" data-testid="complement-text" />
          <Button onClick={send} disabled={!ph.length && !txt.trim()} className="bg-[#0E2D43]" data-testid="complement-submit">Ajouter au dossier</Button>
        </div>
      )}
    </div>
  );
}
